import React, { useState, useEffect, useCallback } from "react";
import {
  Avatar,
  Typography,
  Card,
  Container,
  CardContent,
  useMediaQuery,
  Box,
  Stack,
  Fade,
  Alert,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
} from "@mui/material";
import PersonIcon from '@mui/icons-material/Person';
import BadgeIcon from '@mui/icons-material/Badge';
import BookmarkIcon from "@mui/icons-material/Bookmark";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import HourlyMetricsDisplay from "./metrics/hourly_metrics_display";
import AdsClickIcon from '@mui/icons-material/AdsClick';
import AssignmentIcon from '@mui/icons-material/Assignment';
import BarChartIcon from '@mui/icons-material/BarChart';
import GroupIcon from '@mui/icons-material/Group';
import InstagramIcon from '@mui/icons-material/Instagram';
import YouTubeIcon from '@mui/icons-material/YouTube';

const Dashboard = ({
  usersData,
  setUsersData,
  metricsData,
  setMetricsData,
  bookingsData,
  loadingBookings
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState("Overall");
  const [dashboardView, setDashboardView] = useState("Overview");
  const [openPopup, setOpenPopup] = useState(false);
  const isMobile = useMediaQuery("(max-width: 700px)");
  const isTablet = useMediaQuery("(max-width: 1100px)")
  const [realTimeBookingCounts, setRealTimeBookingCounts] = useState({});
  const [useRealTimeCounts, setUseRealTimeCounts] = useState(false);

  const convertToEST = (date) => {
    const utcOffset = -5 * 60 * 60 * 1000;
    return new Date(date.getTime() + utcOffset);
  };

  useEffect(() => {
    const fetchRealTimeBookings = async () => {
      try {
        const token = localStorage.getItem("token");
        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/bookings-count`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (!response.ok) {
          console.error(
            `Error fetching real-time bookings: ${response.status} ${response.statusText}`
          );
          return;
        }

        const data = await response.json();
        if (data.data) {
          setRealTimeBookingCounts(data.data);
          setUseRealTimeCounts(true);
        }
      } catch (error) {
        console.error("Failed to fetch real-time booking counts:", error);
      }
    };

    fetchRealTimeBookings();
  }, []);

  const formatBookingData = (bookingsLog) => {
    if (!bookingsLog) return [];

    return Object.entries(bookingsLog)
      .map(([name, bookings]) => ({
        name,
        ...bookings[0],
      }))
      .sort(
        (a, b) => new Date(b.requested_time) - new Date(a.requested_time)
      );
  };

  const getResetTime = useCallback(() => {
    const now = new Date();
    const estNow = convertToEST(now);

    if (viewMode === "Daily") {
      const today9AM = new Date(estNow);
      today9AM.setHours(9, 0, 0, 0);
      if (estNow < today9AM) {
        today9AM.setDate(today9AM.getDate() - 1);
      }
      return today9AM;
    } else if (viewMode === "Weekly") {
      const monday9AM = new Date(estNow);
      monday9AM.setDate(estNow.getDate() - estNow.getDay() + 1);
      monday9AM.setHours(9, 0, 0, 0);
      if (estNow < monday9AM) {
        monday9AM.setDate(monday9AM.getDate() - 7);
      }
      return monday9AM;
    }
    return null;
  }, [viewMode]);

  const getFilteredInteractions = useCallback(
    (interactionLog) => {
      if (!interactionLog) return 0;

      const resetTime = getResetTime();
      if (!resetTime) {
        return Object.values(interactionLog).flat().length;
      }

      return Object.values(interactionLog)
        .flat()
        .filter((timestamp) => {
          const date = convertToEST(new Date(timestamp));
          return date >= resetTime;
        }).length;
    },
    [getResetTime]
  );

  const getFilteredBookings = useCallback((bookingsLog, range) => {
    if (!bookingsLog) return 0;

    const now = new Date();
    const startOfDay = new Date(now);
    startOfDay.setHours(0, 0, 0, 0);

    const startOfWeek = new Date(now);
    const dayOfWeek = startOfWeek.getDay();
    startOfWeek.setDate(now.getDate() - dayOfWeek + 1);
    startOfWeek.setHours(0, 0, 0, 0);

    const rangeStart = range === "daily" ? startOfDay : startOfWeek;

    return Object.values(bookingsLog)
      .flat()
      .filter(({ date_added }) => {
        const date = new Date(date_added);
        return date >= rangeStart && date <= now;
      }).length;
  }, []);

  const calculateWeeklyOrOverallHours = (
    firstInteractionDate,
    lastInteractionDate
  ) => {
    const now = new Date();
    const estNow = convertToEST(now);
    let totalHours = 0;

    const startOfPeriod =
      viewMode === "Weekly"
        ? new Date(
          Math.max(
            new Date(
              estNow.setDate(estNow.getDate() - estNow.getDay() + 1)
            ).getTime(),
            new Date(firstInteractionDate).getTime()
          )
        )
        : new Date(firstInteractionDate);

    startOfPeriod.setHours(9, 0, 0, 0);

    for (
      let d = new Date(startOfPeriod);
      d <= estNow;
      d.setDate(d.getDate() + 1)
    ) {
      const dayOfWeek = d.getDay();
      if (dayOfWeek === 0 || dayOfWeek === 6) {
        continue;
      }

      const dayStart = new Date(d);
      dayStart.setHours(9, 0, 0, 0);
      const dayEnd = new Date(d);
      dayEnd.setHours(19, 0, 0, 0);

      if (estNow < dayStart) break;

      if (
        d.toDateString() === new Date(lastInteractionDate).toDateString()
      ) {
        totalHours += Math.min(
          (new Date(lastInteractionDate) - dayStart) / (1000 * 60 * 60),
          10
        );
      } else if (estNow < dayEnd) {
        totalHours += Math.min((estNow - dayStart) / (1000 * 60 * 60), 10);
      } else {
        totalHours += 10;
      }
    }

    return totalHours;
  };

  useEffect(() => {
    const fetchUsersData = async () => {
      const token = localStorage.getItem("token");

      if (!token) {
        setError("Authorization token is missing.");
        setLoading(false);
        return;
      }

      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/users`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (!response.ok) {
          throw new Error(`Error: ${response.statusText}`);
        }

        const data = await response.json();

        const sortedData = data.users
          .map((user) => ({
            ...user,
            dailyBookings: getFilteredBookings(user.bookings_log, "daily"),
            weeklyBookings: getFilteredBookings(user.bookings_log, "weekly"),
            overallBookings: Object.values(user.bookings_log || {}).flat()
              .length,
            interactions: getFilteredInteractions(user.interaction_log),
          }))
          .sort((a, b) => {
            if (viewMode === "Daily") {
              return b.dailyBookings - a.dailyBookings;
            }
            if (viewMode === "Weekly") {
              return b.weeklyBookings - a.weeklyBookings;
            }
            return b.overallBookings - a.overallBookings;
          });

        setUsersData(sortedData);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchUsersData();
  }, [viewMode, getFilteredBookings, getFilteredInteractions, setUsersData]);

  const getUserBookingsCount = useCallback(
    (username) => {
      if (
        useRealTimeCounts &&
        realTimeBookingCounts[username] !== undefined
      ) {
        const count = realTimeBookingCounts[username];
        return count;
      }

      const userObj = usersData?.find((user) => user.username === username);

      if (!userObj) {
        return 0;
      }

      if (userObj.bookings_log) {
        const count = Object.values(userObj.bookings_log).flat().length;
        return count;
      }

      if (typeof userObj.bookings === "number") {
        return userObj.bookings;
      }

      return 0;
    },
    [usersData, realTimeBookingCounts, useRealTimeCounts]
  );

  const getLatestInteraction = (user) => {
    if (
      !user.interaction_log ||
      Object.values(user.interaction_log).length === 0
    )
      return null;

    const allTimestamps = Object.entries(user.interaction_log).flatMap(
      ([userKey, timestamps]) =>
        timestamps.map((timestamp) => ({
          userKey,
          timestamp,
        }))
    );

    return allTimestamps.reduce((latest, current) =>
      new Date(current.timestamp) > new Date(latest.timestamp)
        ? current
        : latest
    );
  };

  const handleDashboardViewChange = (event, newView) => {
    if (newView !== null) {
      setDashboardView(newView);
    }
  };

  if (loading) {
    return (
      <Container
        style={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          backgroundColor: "#212121",
          color: "#98FF98",
        }}
      >
        <CircularProgress sx={{ color: "#98FF98" }} />
      </Container>
    );
  }

  if (error) {
    return (
      <Box sx={{ textAlign: "center", marginTop: "2rem" }}>
        <Alert severity="error">{error}</Alert>
        <Typography variant="h6">
          Failed to load metrics data. Please try again later.
        </Typography>
      </Box>
    );
  }

  const handleClosePopup = () => {
    setOpenPopup(false);
  };

  const formatLastActive = (lastActive) => {
    if (!lastActive) return "-";

    const now = new Date();
    const lastActiveDate = new Date(lastActive);
    const diffMs = now - lastActiveDate;
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${diffDays}d ago`;
  };

  const renderUserCard = (user, index) => {
    const bookingsCount = getUserBookingsCount(user.username);
    const latestInteraction = getLatestInteraction(user);

    const assignmentProgress = Math.min(100, Math.max(0, bookingsCount));

    return (
      <Card
        key={index}
        sx={{
          backgroundColor: "#1e1e1e",
          borderRadius: "10px",
          boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
          overflow: "hidden",
          border: "1px solid rgba(255,255,255,0.05)",
          height: "100%",
        }}
      >
        <Box
          sx={{
            position: "relative",
            height: "24px",
            background: "#333",
            width: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Box
            sx={{
              position: "absolute",
              height: "100%",
              width: `${assignmentProgress}%`,
              background:
                assignmentProgress < 25
                  ? "linear-gradient(90deg,rgb(252, 135, 135, 0.6) 0%,rgb(214, 96, 96, 0.6) 100%)"
                  : assignmentProgress < 50
                    ? "linear-gradient(90deg,rgb(255, 255, 152, 0.6) 0%,rgb(214, 214, 112, 0.6) 100%)"
                    : assignmentProgress < 75
                      ? "linear-gradient(90deg,rgb(203, 255, 151, 0.6) 0%,rgb(170, 204, 112, 0.6) 100%)"
                      : "linear-gradient(90deg,rgb(152, 255, 152, 0.6) 0%,rgb(112, 214, 112, 0.6) 100%)",
              transition: "width 1s ease-in-out",
              left: 0,
              top: 0,
            }}
          />
          <Typography
            sx={{
              textAlign: "center",
              textShadow: "1px 1px 3px #000",
              position: "relative",
              zIndex: 1,
              fontSize: "15px",
              fontWeight: "bold",
              color: "#fff",
              display: "flex",
              alignItems: "center",
              gap: "4px",

              mb: "-1px",
            }}
          >
            <AssignmentIcon
              sx={{
                mt: "-2px",
                fontSize: "medium",
                filter: "drop-shadow(1px 1px 3px rgba(0, 0, 0, 1))",
              }}
            />{" "}
            {assignmentProgress}%
          </Typography>
        </Box>
        <CardContent>
          <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
            <Avatar
              alt={user.username}
              src={user.avatar || ""}
              sx={{
                backgroundColor: "#2a2a2a",
                color: "#444",
                width: 40,
                height: 40,
                border: "1.5px solid #444",
                mr: 2,
                filter: "drop-shadow(1px 1px 8px #181818)",
              }}
            >
              <PersonIcon />
            </Avatar>
            <Box sx={{ flex: 1 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    color: "#bbb",

                    fontSize: 14,
                    textShadow: "1px 1px 4px #111",
                  }}
                >
                  Username
                </Typography>
              </Box>
              <Typography
                variant="h6"
                sx={{
                  color: "#98FF98",

                  fontSize: 18,
                  textShadow: "1px 1px 4px #111",
                }}
              >
                {user.username}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ mb: 2, ml: 1 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
              }}
            >
              <AccessTimeIcon
                sx={{
                  color: "#ccc",
                  fontSize: "0.9rem",
                  mr: 0.5,
                  mt: "-2px",
                  filter: "drop-shadow(1px 1px 4px #111)",
                }}
              />
              <Typography
                variant="caption"
                sx={{
                  color: "#bbb",

                  fontSize: 14,
                  textShadow: "1px 1px 4px #111",
                }}
              >
                Last Active
              </Typography>
            </Box>
            <Typography
              variant="body1"
              sx={{
                ml: "1px",

                fontSize: 16,
                color: "#bbb",
                textShadow: "1px 1px 4px #111",
              }}
            >
              {formatLastActive(user.lastActive)}
            </Typography>
          </Box>

          <Divider sx={{ my: 1.5, borderColor: "rgba(255,255,255,0.08)" }} />

          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <Box sx={{ textAlign: "center", flex: 1 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mb: 0.5,
                }}
              >
                <BookmarkIcon
                  sx={{
                    color: "#ffab40",
                    fontSize: "0.9rem",
                    mt: "-2px",
                    mr: 0.5,
                    filter: "drop-shadow(1px 1px 4px #111)",
                  }}
                />
                <Typography
                  variant="caption"
                  sx={{
                    color: "#bbb",

                    fontSize: 14,
                    textShadow: "1px 1px 4px #111",
                  }}
                >
                  Bookings
                </Typography>
              </Box>
              <Typography
                variant="h6"
                sx={{
                  color: "#ffab40",

                  fontSize: 18,
                  textShadow: "1px 1px 4px #111",
                }}
              >
                {bookingsCount}
              </Typography>
            </Box>

            <Box sx={{ textAlign: "center", flex: 1 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mb: 0.5,
                }}
              >
                <AdsClickIcon
                  sx={{
                    color: "#64b5f6",
                    fontSize: "0.95rem",
                    mt: "-3px",
                    mr: 0.5,
                    filter: "drop-shadow(1px 1px 4px #111)",
                  }}
                />
                <Typography
                  variant="caption"
                  sx={{
                    color: "#bbb",

                    fontSize: 14,
                    textShadow: "1px 1px 4px #111",
                  }}
                >
                  Interactions
                </Typography>
              </Box>
              <Typography
                variant="h6"
                sx={{
                  color: "#64b5f6",

                  fontSize: 18,
                  textShadow: "1px 1px 4px #111",
                }}
              >
                {user.profile_interactions}
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>
    );
  };

  return (
    <Box sx={{ backgroundColor: "#181818", minHeight: "100vh", pb: 4 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          pt: isTablet ? 2 : 4,
          pb: isTablet ? 0 : 2,
          backgroundColor: "#181818",
        }}
      >
        <ToggleButtonGroup
          value={dashboardView}
          exclusive
          onChange={handleDashboardViewChange}
          sx={{
            borderRadius: "12px",
            border: "1px solid rgba(255,255,255,0.1)",
            "& .MuiToggleButton-root": {
              color: "#bbb",
              border: "none",
              borderRadius: "10px",

              fontSize: isMobile ? "14px" : "16px",
              fontWeight: 700,
              letterSpacing: "0.5px",
              textTransform: "none",
              textShadow: "1px 1px 3px #000",
              transition: "all 0.3s ease",
              "&:hover": {
                backgroundColor: "rgba(152, 255, 152, 0.1)",
                color: "#98FF98",
              },
              "&.Mui-selected": {
                backgroundColor: "#98FF98",
                color: "#181818",
                fontWeight: 600,
                textShadow: "1px 1px 3px rgba(0, 0, 0, 0.4)",
                "&:hover": {
                  backgroundColor: "#85de85",
                  color: "#181818",
                  textShadow: "1px 1px 3px rgba(0, 0, 0, 0.4)",
                },
              },
            },
          }}
        >
          <ToggleButton value="Overview" sx={{ display: "flex", m: 1, width: isMobile ? "140px" : "200px" }}>
            <BarChartIcon sx={{ mr: 1, fontSize: isMobile ? "1.0rem" : "1.1rem" }} />
            Overview
          </ToggleButton>
          <ToggleButton value="Team" sx={{ display: "flex", m: 1, width: isMobile ? "140px" : "200px" }}>
            <GroupIcon sx={{ mr: 1, fontSize: isMobile ? "1.0rem" : "1.1rem" }} />
            Team
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>
      <Fade in timeout={500} key={dashboardView}>
        <Box>
          {dashboardView === "Overview" ? (
            <HourlyMetricsDisplay />
          ) : (
            <Box sx={{
              pb: 10,
              mt: 2, backgroundColor: "#181818"
            }}>
              {isMobile ? (
                <Stack spacing={2} sx={{ px: 1 }}>
                  {usersData.map((user, index) => renderUserCard(user, index))}
                </Stack>
              ) : (
                <Box sx={{
                  px: 2,
                }}>
                  <Grid container spacing={isTablet ? 2 : 4} >
                    {usersData.map((user, index) => (
                      <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
                        {renderUserCard(user, index)}
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              )}
            </Box>
          )}
        </Box>
      </Fade>
    </Box >
  );
};

export default Dashboard;
